/**
 * Quick Start — 14-дневное испытание.
 *
 * По ТЗ:
 *   - Отсчёт идёт от ДАТЫ РЕГИСТРАЦИИ (не от первого депозита)
 *   - Нужно привести 4 партнёра с ФАКТИЧЕСКИМ депозитом
 *   - Приз: бонусный слот
 *   - Бонусные слоты НЕ входят в оборот сети
 *   - Бонусные слоты НЕ платят рефералку upline
 *   - Failed — повторить нельзя
 *
 * Структура:
 *   1. Hero с countdown таймером + прогресс 0/4 партнёров
 *   2. 4 ячейки партнёров (заполненные / пустые)
 *   3. Карточка награды (бонусный слот) с пояснениями
 *   4. Подсказки FAQ в аккордеонах
 *
 * Работает на моковых данных; когда backend ready — swap в задаче 11.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, type Variants } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Zap,
  Check,
  UserPlus,
  Clock,
  Gift,
  Flame,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import {
  GlassSurface,
  GlassPill,
  ClickableCard,
} from '@/components/glass';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import giftAsset from '@/assets/quick-start/gift.webp';

// ────────────────────────────────────────────────────────────────────
// Shape от GET /api/luna/quick-start/status
// ────────────────────────────────────────────────────────────────────
interface QuickStartPartner {
  label: string;
  name?: string;
  depositUsdt: number;
  daysAgo: number;
  hoursAgo?: number;
}

interface QuickStartState {
  registeredAt: string;
  requiredRefs: number;
  days: number;
  partners: QuickStartPartner[];
  state: 'active' | 'completed' | 'failed';
  bonusSlotAmount?: number;
}

// ────────────────────────────────────────────────────────────────────
// Animation
// ────────────────────────────────────────────────────────────────────
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.06 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
  },
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────
export function QuickStart() {
  const { t, i18n } = useTranslation();
  const lang: 'ru' | 'en' = i18n.language === 'ru' ? 'ru' : 'en';
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [apiData, setApiData] = useState<QuickStartState | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [celebrationAmount, setCelebrationAmount] = useState<string>('');
  const [claimError, setClaimError] = useState<string | null>(null);
  const celebrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function handleClaimBonus() {
    if (claiming || celebrating) return;
    setClaiming(true);
    setClaimError(null);
    // Haptic + звук ещё до ответа — мгновенный отклик на тап.
    haptic.heavy();
    uiSounds.claimStart();
    try {
      const { lunaApi } = await import('@/services/api');
      const resp = await lunaApi.quickStart.claimBonus();
      const amount = resp.data.amountUsdt;
      setCelebrationAmount(amount);
      setCelebrating(true);
      // Каскад haptic'ов по волне анимации карточки
      const seq = [300, 900, 1500, 2300];
      seq.forEach((t, i) => {
        setTimeout(
          () => (i % 2 === 0 ? haptic.success() : haptic.medium()),
          t,
        );
      });
      uiSounds.claimSuccess();
      // Overlay закрывается по кнопке «Готово» — никаких таймеров.
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('quickStart.claimError');
      setClaimError(msg);
      haptic.error();
    } finally {
      setClaiming(false);
    }
  }

  useEffect(() => {
    return () => {
      if (celebrationTimerRef.current) {
        clearTimeout(celebrationTimerRef.current);
      }
    };
  }, []);

  // Закрытие overlay по кнопке «Готово» — heavy haptic + refetch state.
  // Никаких авто-закрытий — чтобы юзер сам решал момент продолжения.
  async function handleCelebrationClose() {
    haptic.heavy();
    uiSounds.bellClose();
    setCelebrating(false);
    try {
      const { lunaApi } = await import('@/services/api');
      const res = await lunaApi.quickStart.status();
      const d = res.data as {
        success: boolean;
        state?: {
          status: string;
          windowStartedAt: string;
          windowEndsAt: string;
          currentRefCount: number;
          refsRequired: number;
          partners?: Array<{
            refUserId: number;
            name: string;
            depositUsdt: string;
            daysAgo: number;
            hoursAgo: number;
          }>;
        };
      };
      if (d.success && d.state) {
        const apiPartners = d.state.partners ?? [];
        setApiData({
          registeredAt: d.state.windowStartedAt,
          days: 14,
          requiredRefs: d.state.refsRequired,
          partners: Array.from(
            { length: d.state.currentRefCount },
            (_, i) => {
              const p = apiPartners[i];
              return {
                label: t('quickStart.partnerN', { n: i + 1 }),
                name: p?.name,
                depositUsdt: p ? parseFloat(p.depositUsdt) : 0,
                daysAgo: p?.daysAgo ?? 0,
                hoursAgo: p?.hoursAgo ?? 0,
              };
            },
          ),
          state: d.state.status as 'active' | 'completed' | 'failed',
        });
      }
    } catch {
      /* silent: если не удалось — юзер просто увидит старый стейт */
    }
  }

  useEffect(() => {
    import('@/services/api').then(({ lunaApi }) => {
      lunaApi.quickStart.status().then((res) => {
        const d = res.data as {
          success: boolean;
          state?: {
            status: string;
            windowStartedAt: string;
            windowEndsAt: string;
            currentRefCount: number;
            refsRequired: number;
            partners?: Array<{
              refUserId: number;
              name: string;
              depositUsdt: string;
              daysAgo: number;
              hoursAgo: number;
            }>;
          };
        };
        if (d.success && d.state) {
          const apiPartners = d.state.partners ?? [];
          setApiData({
            registeredAt: d.state.windowStartedAt,
            days: 14,
            requiredRefs: d.state.refsRequired,
            partners: Array.from({ length: d.state.currentRefCount }, (_, i) => {
              const p = apiPartners[i];
              return {
                label: t('quickStart.partnerN', { n: i + 1 }),
                name: p?.name,
                depositUsdt: p ? parseFloat(p.depositUsdt) : 0,
                daysAgo: p?.daysAgo ?? 0,
                hoursAgo: p?.hoursAgo ?? 0,
              };
            }),
            state: d.state.status as 'active' | 'completed' | 'failed',
          });
        }
      }).catch(() => { /* fallback mock */ });
    });
  }, []);

  // До ответа API — честные нули, без фейков
  const data = apiData ?? {
    registeredAt: new Date().toISOString(),
    requiredRefs: 4,
    days: 14,
    partners: [],
    state: 'active' as const,
  };

  const { daysLeft, hoursLeft, pct, done } = useQuickStartCountdown(
    data.registeredAt,
    data.days,
  );

  const progress = Math.min(
    data.requiredRefs,
    data.partners.length,
  );
  const progressPct = (progress / data.requiredRefs) * 100;

  const cells = useMemo(() => {
    const list: (QuickStartPartner | null)[] = [];
    for (let i = 0; i < data.requiredRefs; i++) {
      list.push(data.partners[i] ?? null);
    }
    return list;
  }, [data]);

  const effectiveState: 'active' | 'completed' | 'failed' =
    data.state === 'completed'
      ? 'completed'
      : data.state === 'failed' || done
        ? 'failed'
        : progress >= data.requiredRefs
          ? 'completed'
          : 'active';

  return (
    <motion.div
      className="space-y-5 max-w-md mx-auto pt-2 pb-6"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {/* ─── HERO with countdown ─── */}
      <motion.div variants={itemVariants}>
        <GlassSurface variant="strong" className="p-6 relative overflow-hidden">
          {/* Radial accent */}
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              top: -60,
              right: -60,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, color-mix(in oklab, var(--accent-primary) 40%, transparent) 0%, transparent 65%)',
              opacity: 0.7,
            }}
          />

          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1 font-mono">
              {t('quickStart.kicker')}
            </p>
            <h1 className="font-display text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight mb-4">
              {t('quickStart.title')}
            </h1>

            {/* Countdown */}
            <div
              className="rounded-2xl p-4 mb-4"
              style={{
                background: 'var(--glass-fill)',
                border: '1px solid var(--glass-stroke)',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <Clock
                    size={12}
                    strokeWidth={2.4}
                    className="text-[var(--accent-primary)]"
                  />
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                    {t('quickStart.toFinale')}
                  </span>
                </div>
                <span
                  className="font-display text-[18px] font-extrabold tabular-nums"
                  style={{
                    color:
                      done || effectiveState === 'failed'
                        ? 'var(--status-negative)'
                        : 'var(--accent-primary)',
                  }}
                >
                  {done ? t('quickStart.finished') : t('quickStart.countdown', { days: daysLeft, hours: hoursLeft })}
                </span>
              </div>
              <div
                className="h-[6px] rounded-full overflow-hidden"
                style={{ background: 'var(--glass-stroke)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background:
                      'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
                    boxShadow:
                      '0 0 14px color-mix(in oklab, var(--accent-primary) 60%, transparent)',
                    transition: 'width 0.5s ease-out',
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-[0.14em]">
                <span>{t('quickStart.fromRegDate')}</span>
                <span>{pct.toFixed(0)}%</span>
              </div>
            </div>

            {/* Partners progress */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <UserPlus
                  size={13}
                  strokeWidth={2.2}
                  className="text-[var(--text-secondary)]"
                />
                <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono font-bold">
                  {t('quickStart.partners')}
                </span>
              </div>
              <span
                className="font-mono text-[12px] font-extrabold tabular-nums"
                style={{ color: 'var(--accent-primary)' }}
              >
                {progress} / {data.requiredRefs}
              </span>
            </div>
            <div
              className="h-[6px] rounded-full overflow-hidden mb-3"
              style={{ background: 'var(--glass-stroke)' }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progressPct}%`,
                  background:
                    'linear-gradient(90deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 60%, var(--accent-tertiary)))',
                  boxShadow:
                    '0 0 14px color-mix(in oklab, var(--accent-primary) 60%, transparent)',
                  transition: 'width 0.6s ease-out',
                }}
              />
            </div>

            {/* State badge */}
            <div className="flex items-center">
              {effectiveState === 'active' && (
                <GlassPill tone="lunar" dot compact>
                  ACTIVE
                </GlassPill>
              )}
              {effectiveState === 'completed' && (
                <GlassPill tone="positive" dot compact>
                  COMPLETED
                </GlassPill>
              )}
              {effectiveState === 'failed' && (
                <GlassPill tone="negative" dot compact>
                  FAILED
                </GlassPill>
              )}
            </div>
          </div>
        </GlassSurface>
      </motion.div>

      {/* ─── PARTNER CELLS ─── */}
      <motion.div variants={itemVariants}>
        <ClickableCard
          tone="plasma"
          className="p-5"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-3">
            {t('quickStart.progressOf', { progress, total: data.requiredRefs })}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {cells.map((p, idx) => (
              <PartnerCell key={idx} index={idx + 1} partner={p} t={t} lang={lang} />
            ))}
          </div>

          {/* Claim-бонус кнопка — появляется когда набрано 4/4, но бонус
              ещё не забран (бэк оставляет current_ref_count=4 до клика).
              Не смотрим на effectiveState (он вычисляется на фронте и
              становится 'completed' локально при 4/4) — берём прямое
              значение data.state с бэка: пока оно 'active' и counter
              >= 4, это именно момент "готово к клейму". */}
          {progress >= data.requiredRefs && data.state === 'active' && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
              className="mt-4"
            >
              <button
                type="button"
                disabled={claiming}
                onClick={handleClaimBonus}
                className="w-full rounded-2xl py-4 flex items-center justify-center gap-2 text-[14px] font-extrabold uppercase tracking-[0.1em] text-[#0a0a0f] active:scale-[0.98] transition-transform disabled:opacity-60"
                style={{
                  background:
                    'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 65%, var(--accent-secondary)))',
                  boxShadow:
                    '0 20px 40px -10px color-mix(in oklab, var(--accent-primary) 70%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.55)',
                  animation: claiming
                    ? undefined
                    : 'qs-claim-pulse 1.8s ease-in-out infinite',
                }}
              >
                <Gift size={18} strokeWidth={2.4} />
                {claiming ? t('quickStart.claimLoading') : t('quickStart.claimBtn')}
              </button>
              {claimError && (
                <p className="text-[11px] text-[var(--status-negative)] text-center mt-2">
                  {claimError}
                </p>
              )}
            </motion.div>
          )}
        </ClickableCard>
      </motion.div>

      {/* 🎉 Celebration overlay — 5+ сек анимация конфетти + победного чекмарка */}
      <AnimatePresence>
        {celebrating && (
          <CelebrationOverlay
            amount={celebrationAmount}
            onClose={handleCelebrationClose}
            t={t}
            lang={lang}
          />
        )}
      </AnimatePresence>

      {/* ─── REWARD CARD ─── */}
      <motion.div variants={itemVariants}>
        <ClickableCard
          glow="lunar"
          variant="strong"
          className="p-5"
        >
          <div className="flex items-start gap-3">
            <div
              className="h-12 w-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  'color-mix(in oklab, var(--accent-primary) 18%, transparent)',
                border:
                  '1px solid color-mix(in oklab, var(--accent-primary) 40%, transparent)',
                boxShadow:
                  '0 0 22px -4px color-mix(in oklab, var(--accent-primary) 55%, transparent)',
              }}
            >
              <Gift
                size={20}
                strokeWidth={2}
                className="text-[var(--accent-primary)]"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                {t('quickStart.reward')}
              </p>
              <h3 className="font-display text-[18px] font-extrabold text-[var(--text-primary)] tracking-tight leading-tight mt-0.5">
                {t('quickStart.rewardTitle', { amount: data.bonusSlotAmount })}
              </h3>
              <p className="text-[12px] text-[var(--text-secondary)] leading-snug mt-1">
                {t('quickStart.rewardBody')}
              </p>
            </div>
          </div>
        </ClickableCard>
      </motion.div>

      {/* ─── FAQ-style rules ─── */}
      <motion.div variants={itemVariants} className="space-y-2.5">
        <RulePill
          icon={<Zap size={12} strokeWidth={2.4} />}
          title={t('quickStart.rule1Title')}
          body={t('quickStart.rule1Body')}
        />
        <RulePill
          icon={<Flame size={12} strokeWidth={2.4} />}
          title={t('quickStart.rule2Title')}
          body={t('quickStart.rule2Body')}
        />
        <RulePill
          icon={<AlertTriangle size={12} strokeWidth={2.4} />}
          title={t('quickStart.rule3Title')}
          body={t('quickStart.rule3Body')}
        />
      </motion.div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Cell for one partner
// ────────────────────────────────────────────────────────────────────
function PartnerCell({
  index,
  partner,
  t,
  lang,
}: {
  index: number;
  partner: QuickStartPartner | null;
  t: TFunction;
  lang: 'ru' | 'en';
}) {
  const filled = partner !== null;
  return (
    <div
      className="aspect-[3/4] rounded-2xl p-2 flex flex-col items-center justify-center text-center relative"
      style={{
        background: filled
          ? 'color-mix(in oklab, var(--accent-primary) 14%, transparent)'
          : 'var(--glass-fill)',
        border: filled
          ? '1px solid color-mix(in oklab, var(--accent-primary) 45%, transparent)'
          : '1px dashed var(--glass-stroke)',
        boxShadow: filled
          ? '0 0 18px -4px color-mix(in oklab, var(--accent-primary) 55%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.18)'
          : undefined,
      }}
    >
      {filled ? (
        <>
          <div
            className="h-7 w-7 rounded-full flex items-center justify-center mb-1"
            style={{
              background: 'color-mix(in oklab, var(--status-positive) 30%, transparent)',
              border:
                '1px solid color-mix(in oklab, var(--status-positive) 55%, transparent)',
            }}
          >
            <Check
              size={12}
              strokeWidth={3}
              className="text-[var(--status-positive)]"
            />
          </div>
          <p className="text-[11px] text-[var(--text-primary)] font-semibold truncate max-w-full">
            {partner.name || partner.label}
          </p>
          <p className="text-[10px] font-mono font-bold text-[var(--accent-primary)] mt-0.5">
            {formatDeposit(partner.depositUsdt, lang, t)}
          </p>
          <p className="text-[8px] font-mono text-[var(--text-muted)] mt-0.5">
            {formatJoinedAgo(partner.daysAgo, partner.hoursAgo ?? 0, t, lang)}
          </p>
        </>
      ) : (
        <>
          <div
            className="h-7 w-7 rounded-full flex items-center justify-center mb-1"
            style={{
              background: 'var(--glass-fill)',
              border: '1px dashed var(--glass-stroke)',
            }}
          >
            <span className="text-[11px] font-mono font-extrabold text-[var(--text-muted)]">
              {index}
            </span>
          </div>
          <p className="text-[10px] text-[var(--text-muted)] font-mono uppercase tracking-[0.12em]">
            {t('quickStart.cellEmpty')}
          </p>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Rule pill — compact explainer row
// ────────────────────────────────────────────────────────────────────
function RulePill({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      className="lg-surface rounded-[20px] p-3.5 flex items-start gap-3"
    >
      <div
        className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-[2px]"
        style={{
          background:
            'color-mix(in oklab, var(--accent-primary) 14%, transparent)',
          color: 'var(--accent-primary)',
        }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-display font-extrabold text-[var(--text-primary)] tracking-tight">
          {title}
        </p>
        <p className="text-[11px] text-[var(--text-secondary)] leading-snug mt-0.5">
          {body}
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Countdown hook — computes daysLeft + hoursLeft every 60 seconds
// ────────────────────────────────────────────────────────────────────
function useQuickStartCountdown(registeredAt: string, days: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const start = new Date(registeredAt).getTime();
  const end = start + days * 24 * 60 * 60 * 1000;
  const remainingMs = Math.max(0, end - now);
  const totalMs = days * 24 * 60 * 60 * 1000;
  const pct = Math.min(100, ((totalMs - remainingMs) / totalMs) * 100);
  const daysLeft = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  const hoursLeft = Math.floor(
    (remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000),
  );
  return {
    daysLeft,
    hoursLeft,
    pct,
    done: remainingMs === 0,
  };
}

// Формат депозита: "100 USDT" / "100 USDT" — локализованный
function formatDeposit(n: number, lang: 'ru' | 'en', t: TFunction): string {
  const unit = t('quickStart.depositUnit');
  if (!isFinite(n) || n <= 0) return `— ${unit}`;
  const rounded = Math.round(n * 1000) / 1000;
  const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
  const str = Number.isInteger(rounded)
    ? rounded.toLocaleString(locale)
    : rounded.toLocaleString(locale, { maximumFractionDigits: 3 });
  return `${str} ${unit}`;
}

// "только что" / "2 часа назад" / "3 дня назад" — локализованный.
function formatJoinedAgo(days: number, hours: number, t: TFunction, lang: 'ru' | 'en'): string {
  if (days <= 0 && (hours ?? 0) < 1) return t('quickStart.joinedNow');
  if (days <= 0) {
    const h = hours ?? 0;
    if (h === 1) return t('quickStart.joinedHourOne');
    if (lang === 'ru' && h >= 2 && h <= 4) return t('quickStart.joinedHoursFew', { h });
    return t('quickStart.joinedHoursMany', { h });
  }
  const d = days;
  if (d === 1) return t('quickStart.joinedDayOne');
  if (lang === 'ru' && d >= 2 && d <= 4) return t('quickStart.joinedDaysFew', { d });
  return t('quickStart.joinedDaysMany', { d });
}

// ────────────────────────────────────────────────────────────────────
// CelebrationOverlay — 11-секундная анимация награждения.
//   - Рендерится через React Portal в document.body — иначе родительский
//     motion.div с transform ломает position:fixed (карточка улетает).
//   - Затемнение фона (backdrop)
//   - Большой пульсирующий gift-круг с зелёным glow
//   - 28 конфетти-частиц разлетаются из центра
//   - Строка "+N USDT" выезжает с spring
//   - Подпись "Новое окно открыто · 14 дней"
// Автозакрытие управляется родителем через таймаут 11000ms.
// ────────────────────────────────────────────────────────────────────
function CelebrationOverlay({
  amount,
  onClose,
  t,
  lang,
}: {
  amount: string;
  onClose: () => void;
  t: TFunction;
  lang: 'ru' | 'en';
}) {
  const n = parseFloat(amount || '0');
  const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
  const displayAmount = Number.isFinite(n) ? n.toLocaleString(locale) : amount;
  if (typeof document === 'undefined') return null;
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center px-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ opacity: { duration: 0.6, ease: [0.32, 0.72, 0, 1] } }}
    >
      {/* Полноэкранный блюр-фон (затемнение + лёгкий акцент) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, color-mix(in oklab, var(--accent-primary) 16%, transparent) 0%, rgba(6,6,12,0.82) 70%)',
          backdropFilter: 'blur(14px) saturate(140%)',
          WebkitBackdropFilter: 'blur(14px) saturate(140%)',
        }}
      />

      {/* ────── ГЛОБАЛЬНЫЕ КОЛЬЦА — расходятся по всему экрану от медальки ────── */}
      <div
        className="absolute left-1/2 top-1/2 z-0 flex items-center justify-center pointer-events-none"
        style={{ transform: 'translate(-50%, calc(-50% - 110px))' }}
      >
        {[0, 0.6, 1.2, 1.8].map((delay) => (
          <div
            key={delay}
            className="qs-celebration-ring"
            style={{ animationDelay: `${delay}s` }}
          />
        ))}
      </div>

      {/* ────── ГЛОБАЛЬНОЕ КОНФЕТТИ — разлетается по всему экрану ────── */}
      <div
        className="absolute left-1/2 top-1/2 z-0 pointer-events-none"
        style={{
          width: 0,
          height: 0,
          transform: 'translate(-50%, calc(-50% - 110px))',
        }}
      >
        {Array.from({ length: 28 }).map((_, i) => {
          const angle = (i / 28) * Math.PI * 2;
          const distance = 280 + (i % 4) * 40;
          const dx = Math.cos(angle) * distance;
          const dy = Math.sin(angle) * distance;
          const hue = i % 3 === 0 ? 'var(--accent-primary)'
            : i % 3 === 1 ? 'var(--accent-secondary)'
            : 'var(--status-positive)';
          return (
            <span
              key={i}
              className="qs-confetti"
              style={
                {
                  '--dx': `${dx}px`,
                  '--dy': `${dy}px`,
                  background: hue,
                  boxShadow: `0 0 14px ${hue}`,
                  animationDelay: `${i * 0.05}s`,
                } as CSSProperties
              }
            />
          );
        })}
      </div>

      {/* ────── ВЫДЕЛЕННАЯ КАРТОЧКА ────── */}
      {/* overflow НЕ hidden: свечение медальки свободно выходит за
          пределы карточки, без «невидимых границ» */}
      <motion.div
        className="relative z-10 w-full max-w-[340px] rounded-[28px]"
        initial={{ y: 30, opacity: 0, scale: 0.94 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 20, opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.55, ease: [0.32, 1.2, 0.32, 1] }}
        style={{
          background:
            'linear-gradient(180deg, rgba(18,18,28,0.96) 0%, rgba(10,10,18,0.98) 100%)',
          border: '1px solid var(--glass-stroke-strong)',
          boxShadow:
            '0 40px 90px -20px rgba(0,0,0,0.75), 0 0 80px -20px color-mix(in oklab, var(--accent-primary) 55%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.08)',
        }}
      >
        {/* Контент карточки */}
        <div className="relative flex flex-col items-center gap-4 px-6 pt-10 pb-6">
          {/* Медаль */}
          <motion.div
            initial={{ scale: 0.4, rotate: -20, opacity: 0 }}
            animate={{
              scale: [0.4, 1.08, 1],
              rotate: [-20, 3, 0],
              opacity: 1,
              y: [0, -5, 0],
            }}
            transition={{
              scale:   { duration: 1.1, delay: 0.2, times: [0, 0.65, 1], ease: [0.34, 1.56, 0.64, 1] },
              rotate:  { duration: 1.1, delay: 0.2, times: [0, 0.65, 1] },
              opacity: { duration: 0.5, delay: 0.2 },
              y:       { duration: 3.2, delay: 1.2, repeat: Infinity, ease: 'easeInOut' },
            }}
            className="relative h-32 w-32 flex items-center justify-center"
          >
            {/* Сияние от медальки — отдельный div с radial-gradient.
                НЕ используем filter/drop-shadow: в iOS WebKit при scale-анимации
                filter-буфер обрезается → видны «жёсткие края свечения». Чистый
                radial-gradient свободно растекается без границ. */}
            <div
              aria-hidden
              className="absolute pointer-events-none rounded-full"
              style={{
                left: '50%',
                top: '50%',
                width: '360px',
                height: '360px',
                transform: 'translate(-50%, -50%)',
                background:
                  'radial-gradient(circle, color-mix(in oklab, var(--accent-primary) 55%, transparent) 0%, color-mix(in oklab, var(--accent-primary) 18%, transparent) 32%, transparent 62%)',
              }}
            />
            <img
              src={giftAsset}
              alt=""
              aria-hidden
              draggable={false}
              className="relative h-32 w-32 object-contain pointer-events-none select-none"
            />
            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: [0, 1.3, 1, 1.15, 1], rotate: [-30, 20, 0, 12, 0] }}
              transition={{ duration: 2.0, delay: 0.9, times: [0, 0.25, 0.5, 0.75, 1] }}
              className="absolute -top-1 -right-1"
              style={{
                filter: 'drop-shadow(0 0 10px var(--accent-primary))',
              }}
            >
              <Sparkles
                size={26}
                className="text-[var(--accent-primary)]"
              />
            </motion.div>
          </motion.div>

          {/* Заголовок + сумма */}
          <motion.div
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, delay: 0.45, ease: [0.32, 0.72, 0, 1] }}
            className="text-center"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] font-mono mb-1">
              {t('quickStart.celebration.kicker')}
            </p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="font-display text-[40px] font-extrabold text-[var(--text-primary)] tracking-tight tabular-nums leading-none">
                +{displayAmount}
              </span>
              <span className="text-[15px] font-extrabold text-[var(--accent-primary)]">
                USDT
              </span>
            </div>
            <p className="text-[12px] text-[var(--text-secondary)] mt-2 font-semibold">
              {t('quickStart.celebration.sub')}
            </p>
          </motion.div>

          {/* Плашка нового окна */}
          <motion.div
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, delay: 0.85, ease: [0.32, 0.72, 0, 1] }}
            className="w-full rounded-2xl px-4 py-2.5 text-center"
            style={{
              background: 'color-mix(in oklab, var(--accent-primary) 10%, transparent)',
              border: '1px solid color-mix(in oklab, var(--accent-primary) 30%, transparent)',
            }}
          >
            <p className="text-[10.5px] text-[var(--accent-primary)] font-mono uppercase tracking-[0.14em] font-bold">
              {t('quickStart.celebration.newWindow')}
            </p>
            <p className="text-[9.5px] text-[var(--text-muted)] mt-0.5">
              {t('quickStart.celebration.newWindowHint')}
            </p>
          </motion.div>

          {/* Кнопка «Готово» — heavy haptic + refetch state */}
          <motion.button
            type="button"
            onClick={onClose}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, delay: 1.3, ease: [0.32, 0.72, 0, 1] }}
            className="w-full mt-1 rounded-[var(--radius-pill)] font-display text-[13px] font-extrabold tracking-[0.16em] uppercase text-[var(--text-primary)] flex items-center justify-center transition-transform active:scale-[0.95]"
            style={{
              height: 52,
              background:
                'linear-gradient(180deg, color-mix(in oklab, var(--accent-primary) 24%, transparent), color-mix(in oklab, var(--accent-primary) 8%, transparent))',
              border: '1px solid color-mix(in oklab, var(--accent-primary) 55%, transparent)',
              boxShadow:
                'inset 0 1px 0 0 rgba(255,255,255,0.2), 0 10px 28px -8px color-mix(in oklab, var(--accent-primary) 55%, transparent)',
            }}
          >
            {t('quickStart.celebration.done')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
